import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Sparkles } from 'lucide-react';
import { SplineScene } from './SplineScene';
import { PeriscanLogo } from './PeriscanLogo';
import { Aurora } from './animations/Aurora';
import { BlurText } from './animations/BlurText';
import { DecryptedText } from './animations/DecryptedText';
import { Magnet } from './animations/Magnet';
/**
 * Two-act hero.
 *
 *   Act 1 (first viewport): the brand reveal — REBORN 2026 badge + logo,
 *   centered, full screen. Nothing else competes for attention.
 *
 *   Act 2 (second viewport): scroll reveals the headline, copy, and CTAs.
 *   The logo and badge shrink + drift upward as Act 2 takes over.
 *
 * Implementation: a 2× viewport tall section with a sticky inner stage.
 * scrollYProgress inside the section drives the cross-fade between acts.
 */
export function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end end']
  });
  // === Background scene parallax (gentle) ===
  const sceneScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const sceneOpacity = useTransform(scrollYProgress, [0, 0.7, 1], [1, 0.4, 0.15]);
  // === Act 1 (logo + badge) — present at start, recedes on scroll ===
  const act1Opacity = useTransform(scrollYProgress, [0, 0.35, 0.55], [1, 1, 0]);
  const act1Scale = useTransform(scrollYProgress, [0, 0.55], [1, 0.55]);
  const act1Y = useTransform(scrollYProgress, [0, 0.55], [0, -160]);
  const act1Blur = useTransform(scrollYProgress, [0.3, 0.55], [0, 8]);
  // === Act 2 (headline + CTAs) — hidden at start, appears on scroll ===
  const act2Opacity = useTransform(scrollYProgress, [0.35, 0.6], [0, 1]);
  const act2Y = useTransform(scrollYProgress, [0.35, 0.7], [60, 0]);
  // === Scroll cue visibility ===
  const cueOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  return <section ref={ref} className="relative w-full" style={{
    height: '200vh'
  }} data-id="element-491">
      {/* Sticky stage — pinned to viewport throughout the hero */}
      <div className="sticky top-0 h-screen w-full overflow-hidden" data-id="element-492">
        {/* Aurora background */}
        <Aurora intensity={0.35} data-id="element-493" />

        {/* 3D Scene */}
        <motion.div style={{
        scale: sceneScale,
        opacity: sceneOpacity
      }} className="absolute inset-0 pointer-events-none" data-id="element-494">
          <SplineScene data-id="element-495" />
        </motion.div>

        {/* Grid texture */}
        <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" data-id="element-496" />

        {/* Vignette */}
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, transparent 25%, rgba(5,6,8,0.6) 65%, #050608 95%)'
      }} data-id="element-497" />

        {/* === ACT 1 — Brand reveal (logo + badge), centered === */}
        <motion.div style={{
        opacity: act1Opacity,
        scale: act1Scale,
        y: act1Y,
        filter: useTransform(act1Blur, b => `blur(${b}px)`)
      }} className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6" data-id="element-498">
          {/* REBORN 2026 badge */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="mb-10" data-id="element-499">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1a8fd4]/10 border border-[#1a8fd4]/20 text-[#1a8fd4] text-xs font-mono uppercase tracking-widest backdrop-blur-sm" data-id="element-500">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1a8fd4] animate-pulse" data-id="element-501" />
              <DecryptedText text="REBORN 2026" speed={60} animateOn="mount" data-id="element-502" />
            </span>
          </motion.div>

          {/* Logo */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.92
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.1,
          ease: 'easeOut'
        }} className="relative flex items-center justify-center" data-id="element-503">
            <motion.div className="absolute inset-0 bg-[#1a8fd4]/30 blur-[100px] rounded-full" animate={{
            scale: [1, 1.15, 1],
            opacity: [0.6, 1, 0.6]
          }} transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-504" />
            <PeriscanLogo variant="lockup" size={200} markColor="#1a8fd4" textColor="#ffffff" animated className="relative h-24 md:h-36 lg:h-44 w-auto drop-shadow-[0_0_60px_rgba(26,143,212,0.5)]" data-id="element-505" />
          </motion.div>
        </motion.div>

        {/* === ACT 2 — Headline + CTAs, revealed by scroll === */}
        <motion.div style={{
        opacity: act2Opacity,
        y: act2Y
      }} className="absolute inset-0 z-10 flex items-center" data-id="element-506">
          <div className="max-w-7xl mx-auto w-full px-6 lg:px-8" data-id="element-507">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end" data-id="element-508">
              {/* Headline */}
              <div className="lg:col-span-8" data-id="element-509">
                <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-semibold tracking-[-0.03em] leading-[0.95] text-white" data-id="element-510">
                  <BlurText text="Periscan is" splitBy="word" delay={0.1} data-id="element-511" />
                  <br data-id="element-512" />
                  <span className="text-white/40" data-id="element-513">
                    <BlurText text="coming back." splitBy="word" delay={0.4} data-id="element-514" />
                  </span>
                </h1>
                <motion.p initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-100px'
              }} transition={{
                duration: 0.8,
                delay: 0.6
              }} className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed" data-id="element-515">
                  Continuous security validation, priced like SaaS. Find the
                  paths that could actually compromise you, fix what matters,
                  and prove the risk is gone.
                </motion.p>
              </div>

              {/* Tagline + CTAs */}
              <motion.div initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.8,
              delay: 0.8
            }} className="lg:col-span-4 space-y-6" data-id="element-516">
                <div className="space-y-1.5 pl-5 border-l border-[#1a8fd4]/40" data-id="element-517">
                  <div className="text-sm font-medium text-white" data-id="element-518">
                    <DecryptedText text="Find the path." speed={40} data-id="element-519" />
                  </div>
                  <div className="text-sm font-medium text-white" data-id="element-520">
                    <DecryptedText text="Fix the risk." speed={40} data-id="element-521" />
                  </div>
                  <div className="text-sm font-medium text-[#1a8fd4]" data-id="element-522">
                    <DecryptedText text="Prove it's gone." speed={40} data-id="element-523" />
                  </div>
                </div>

                <div className="flex flex-col gap-3" data-id="element-524">
                  <Magnet strength={0.2} data-id="element-525">
                    <a href="#early-access" className="group relative overflow-hidden flex items-center justify-between gap-3 px-5 py-3.5 bg-white text-black font-medium rounded-full text-sm hover:bg-white/90 transition-colors w-full" data-id="element-526">
                      <span className="relative z-10" data-id="element-527">Get Early Access</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 relative z-10" data-id="element-528" />
                      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-[#1a8fd4]/20 to-transparent" data-id="element-529" />
                    </a>
                  </Magnet>
                  <Magnet strength={0.15} data-id="element-530">
                    <a href="#early-access" className="group flex items-center justify-between gap-3 px-5 py-3.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-full text-sm transition-colors backdrop-blur-sm w-full" data-id="element-531">
                      <span data-id="element-532">Get Your First Attack-Path Report</span>
                      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-533" />
                    </a>
                  </Magnet>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Scroll cue — visible during Act 1, fades out as Act 2 begins */}
        <motion.div style={{
        opacity: cueOpacity
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3 pointer-events-none" data-id="element-534">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50" data-id="element-535">
            Scroll to continue
          </span>
          <motion.div animate={{
          y: [0, 8, 0],
          opacity: [0.4, 1, 0.4]
        }} transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="w-px h-10 bg-gradient-to-b from-[#1a8fd4] to-transparent" data-id="element-536" />
        </motion.div>
      </div>
    </section>;
}